import React, { useEffect } from 'react';
import { Services } from 'services';
import { AuthPageLayout } from 'components/layout';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Schemas, useFormValidation } from 'utils/validation';
import { Routing } from 'global/routing';
import { Box, Button, Link, TextField } from '@clariness/clarikit-react';
import { IdMeButton } from 'components/IdMeButton';
import { Auth0SsoButton } from 'components/Auth0SsoButton';
import { OrSeparator } from 'components/OrSeparator';
import { Environment } from 'global/environment';

type LoginForm = {
  email: string;
  password: string;
};

export const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormValidation<LoginForm>();
  const intl = useIntl();
  const history = useHistory();
  const { login, isLoading } = Services.Auth.useLogin();
  const forgotPasswordPath = Routing.FORGOT_PASSWORD.getPath();

  useEffect(() => {
    sessionStorage.clear();
    localStorage.clear();
  }, []);

  const auth0SsoEnabled = Environment.AUTH0_SSO_ENABLED === 'true';
  const idmeEnabled = Environment.IDME_ENABLED === 'true';
  const emailLabel = intl.formatMessage({
    id: 'login.email.placeholder',
    defaultMessage: 'Email Address',
  });
  const passwordLabel = intl.formatMessage({
    id: 'login.password.placeholder',
    defaultMessage: 'Password',
  });
  const emailRegistration = register('email', Schemas.email(intl));
  const passwordRegistration = register(
    'password',
    Schemas.password(intl, true)
  );

  return (
    <AuthPageLayout>
      <AuthPageLayout.Logo />

      <AuthPageLayout.Heading>
        <FormattedMessage
          id="is.login.heading"
          defaultMessage="Login to ClinLife"
        />
      </AuthPageLayout.Heading>

      <AuthPageLayout.Form
        onSubmit={handleSubmit((data) => login(data.email, data.password))}
      >
        <Box width="100%">
          <TextField
            requiredIndicator
            placeholder={emailLabel}
            name={emailRegistration.name}
            ref={emailRegistration.ref}
            value={watch('email')}
            onChange={(value) => {
              emailRegistration.onChange({
                target: { name: emailRegistration.name, value },
              });
            }}
            onBlur={(event) => {
              if (event) {
                emailRegistration.onBlur(event);
              }
            }}
            error={errors?.email?.message}
            type="email"
            data-test-id="IS-login-email-input"
            autoComplete="email"
            label={emailLabel}
            labelHidden
          />
        </Box>

        <Box width="100%">
          <TextField
            required
            requiredIndicator
            placeholder={passwordLabel}
            name={passwordRegistration.name}
            ref={passwordRegistration.ref}
            value={watch('password') || ''}
            onChange={(value) => {
              passwordRegistration.onChange({
                target: { name: passwordRegistration.name, value },
              });
            }}
            onBlur={(event) => {
              if (event) {
                passwordRegistration.onBlur(event);
              }
            }}
            error={errors?.password?.message}
            type="password"
            showPasswordButton
            data-test-id="IS-login-password-input"
            autoComplete="current-password"
            label={passwordLabel}
            labelHidden
          />
        </Box>

        <Box width="60%">
          <Button
            submit
            fullWidth
            size="l"
            variant="primary"
            colorVariant="green"
            loading={isLoading}
            data-test-id="IS-login-button"
          >
            {intl.formatMessage({
              id: 'is.login.button',
              defaultMessage: 'Login',
            })}
          </Button>
        </Box>
        <Link
          url={forgotPasswordPath}
          onClick={(event) => {
            event.preventDefault();
            history.push(forgotPasswordPath);
          }}
          size="m"
          data-test-id="IS-login-forgot-passwrod-link"
        >
          <Box
            as="span"
            style={{
              fontSize: '18px',
              lineHeight: '26px',
              textDecoration: 'underline',
            }}
          >
            <FormattedMessage
              id="is.login.forgot_password_link"
              defaultMessage="Forgotten password? Click here"
            />
          </Box>
        </Link>
        {auth0SsoEnabled && (
          <>
            <OrSeparator />
            <Auth0SsoButton />
          </>
        )}
        {idmeEnabled && (
          <>
            <OrSeparator />
            <IdMeButton />
          </>
        )}
      </AuthPageLayout.Form>

      <AuthPageLayout.InfoLinks />
      <AuthPageLayout.Footer />
    </AuthPageLayout>
  );
};
